import { GameState } from '../core/GameState';

export interface BuildingDef {
  id: string;
  name: string;
  plural: string;
  desc: string;
  icon: string;
  basePrice: number;
  baseCps: number;
  priceMultiplier: number;
  /** Returns true when this building should be visible in the store. */
  isUnlocked: (state: GameState) => boolean;
}

export const BUILDINGS: BuildingDef[] = [
  {
    id: 'cursor',
    name: 'Cursor',
    plural: 'Cursors',
    desc: 'Autoclicks once every 10 seconds.',
    icon: '👆',
    basePrice: 15,
    baseCps: 0.1,
    priceMultiplier: 1.15,
    isUnlocked: () => true,
  },
  {
    id: 'grandma',
    name: 'Grandma',
    plural: 'Grandmas',
    desc: 'A nice grandma to bake more cookies.',
    icon: '👵',
    basePrice: 100,
    baseCps: 1,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(100),
  },
  {
    id: 'farm',
    name: 'Farm',
    plural: 'Farms',
    desc: 'Grows cookie plants from cookie seeds.',
    icon: '🌾',
    basePrice: 1_100,
    baseCps: 8,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(1_000),
  },
  {
    id: 'mine',
    name: 'Mine',
    plural: 'Mines',
    desc: 'Mines out cookie dough and chocolate chips.',
    icon: '⛏️',
    basePrice: 12_000,
    baseCps: 47,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(10_000),
  },
  {
    id: 'factory',
    name: 'Factory',
    plural: 'Factories',
    desc: 'Produces large quantities of cookies.',
    icon: '🏭',
    basePrice: 130_000,
    baseCps: 260,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(100_000),
  },
  {
    id: 'bank',
    name: 'Bank',
    plural: 'Banks',
    desc: 'Generates cookies from interest.',
    icon: '🏦',
    basePrice: 1_400_000,
    baseCps: 1_400,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(1_000_000),
  },
  {
    id: 'temple',
    name: 'Temple',
    plural: 'Temples',
    desc: 'Full of precious, ancient cookie artifacts.',
    icon: '🛕',
    basePrice: 20_000_000,
    baseCps: 7_800,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(10_000_000),
  },
  {
    id: 'wizard_tower',
    name: 'Wizard Tower',
    plural: 'Wizard Towers',
    desc: 'Summons cookies with magic spells.',
    icon: '🧙',
    basePrice: 330_000_000,
    baseCps: 44_000,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(100_000_000),
  },
  {
    id: 'shipment',
    name: 'Shipment',
    plural: 'Shipments',
    desc: 'Brings in fresh cookies from the cookie planet.',
    icon: '🚀',
    basePrice: 5_100_000_000,
    baseCps: 260_000,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(1e9),
  },
  {
    id: 'alchemy_lab',
    name: 'Alchemy Lab',
    plural: 'Alchemy Labs',
    desc: 'Turns gold into cookies!',
    icon: '⚗️',
    basePrice: 75_000_000_000,
    baseCps: 1_600_000,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(1e10),
  },
  {
    id: 'portal',
    name: 'Portal',
    plural: 'Portals',
    desc: 'Opens a door to the Cookieverse.',
    icon: '🌀',
    basePrice: 1e12,
    baseCps: 10_000_000,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(1e11),
  },
  {
    id: 'time_machine',
    name: 'Time Machine',
    plural: 'Time Machines',
    desc: 'Brings cookies from the past, before they were even eaten.',
    icon: '⏰',
    basePrice: 1.4e13,
    baseCps: 65_000_000,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(1e12),
  },
  {
    id: 'antimatter_condenser',
    name: 'Antimatter Condenser',
    plural: 'Antimatter Condensers',
    desc: 'Condenses the antimatter in the universe into cookies.',
    icon: '⚛️',
    basePrice: 1.7e14,
    baseCps: 430_000_000,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(1e13),
  },
  {
    id: 'prism',
    name: 'Prism',
    plural: 'Prisms',
    desc: 'Converts light itself into cookies.',
    icon: '🔺',
    basePrice: 2.1e15,
    baseCps: 2_900_000_000,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(1e14),
  },
  {
    id: 'chancemaker',
    name: 'Chancemaker',
    plural: 'Chancemakers',
    desc: 'Generates cookies out of thin air through sheer luck.',
    icon: '🎰',
    basePrice: 2.6e16,
    baseCps: 21_000_000_000,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(1e15),
  },
  {
    id: 'fractal_engine',
    name: 'Fractal Engine',
    plural: 'Fractal Engines',
    desc: 'Turns cookies into even more cookies.',
    icon: '🔮',
    basePrice: 3.1e17,
    baseCps: 150_000_000_000,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(1e16),
  },
  {
    id: 'javascript_console',
    name: 'Javascript Console',
    plural: 'Javascript Consoles',
    desc: 'Creates cookies from the very code this game was written in.',
    icon: '💻',
    basePrice: 7.1e18,
    baseCps: 1.1e12,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(1e17),
  },
  {
    id: 'idleverse',
    name: 'Idleverse',
    plural: 'Idleverses',
    desc: 'There are other idle universes out there, and they all have cookies.',
    icon: '🌌',
    basePrice: 1.2e20,
    baseCps: 8.3e12,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(1e18),
  },
  {
    id: 'cortex_baker',
    name: 'Cortex Baker',
    plural: 'Cortex Bakers',
    desc: 'These text-based artificial intelligences dream of cookies.',
    icon: '🧠',
    basePrice: 1.9e21,
    baseCps: 6.4e13,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(1e19),
  },
  {
    id: 'you',
    name: 'You',
    plural: 'Yous',
    desc: 'A you to bake even more cookies.',
    icon: '🪞',
    basePrice: 5.4e22,
    baseCps: 5.1e14,
    priceMultiplier: 1.15,
    isUnlocked: (s) => s.cookiesEarned.gte(1e20),
  },
];
